import { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { FreshShipmentActions } from '@/app/slices/freshShipment';
import { addToast } from '@/app/slices/toast';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FertilizationQCResponse } from '@/rtk-query.gql';
import {
  useFreshShipmentCacheInvalidation,
  useShipGroupListQuery,
  useUpdateQcFreshShipment,
} from '@/services/fresh-shipment/hooks';

import './FreshShipmentQCInformationDialog.css';

type FreshShipmentQCInformationDialogProps = {
  visible: boolean;
  onHide: () => void;
  fertQcData: FertilizationQCResponse;
};

export const FreshShipmentQCInformationDialog = ({
  visible,
  onHide,
  fertQcData,
}: FreshShipmentQCInformationDialogProps) => {
  const dispatch = useDispatch();
  const dialogRef = useRef<Dialog>(null);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement>();
  const isQcSaveFixEnabled = useFeatureFlag('Fresh_Shipment_QC_Save');
  const invalidateCache = useFreshShipmentCacheInvalidation();

  const [params] = useSearchParams();
  const date = params.get('date');
  const siteId = params.get('sId');

  const [qcEHMDate10ml, setQcEHMDate10ml] = useState<Date | null>(null);
  const [qcEHMDate20ml, setQcEHMDate20ml] = useState<Date | null>(null);
  const [qcEHMDate40ml, setQcEHMDate40ml] = useState<Date | null>(null);
  const [qcEHMDate125ml, setqcEHMDate125ml] = useState<Date | null>(null);
  const [qcEHMDate250ml, setQcEHMDate250ml] = useState<Date | null>(null);
  const [qcShippingTubeExpirationDate, setQcShippingTubeExpirationDate] =
    useState<Date | null>(null);

  const { mutateAsync: saveQcFreshShipment, isPending: isQcLoading } =
    useUpdateQcFreshShipment();

  const { data: shipmentGroupsData } = useShipGroupListQuery({
    fertilizationDate: date,
    fertilizationSiteId: siteId as string,
  });

  useEffect(() => {
    if (fertQcData) {
      setQcEHMDate10ml(
        fertQcData.qcEHMDate10ml ? new Date(fertQcData.qcEHMDate10ml) : null,
      );
      setQcEHMDate20ml(
        fertQcData.qcEHMDate20ml ? new Date(fertQcData.qcEHMDate20ml) : null,
      );
      setQcEHMDate40ml(
        fertQcData.qcEHMDate40ml ? new Date(fertQcData.qcEHMDate40ml) : null,
      );
      setQcEHMDate250ml(
        fertQcData.qcEHMDate250ml ? new Date(fertQcData.qcEHMDate250ml) : null,
      );
      setqcEHMDate125ml(
        fertQcData.qcEHMDate125ml ? new Date(fertQcData.qcEHMDate125ml) : null,
      );
      setQcShippingTubeExpirationDate(
        fertQcData.qcShippingTubeExpirationDate
          ? new Date(fertQcData.qcShippingTubeExpirationDate)
          : null,
      );
    }
  }, [dispatch, fertQcData]);

  const saveQcData = async (shipGroup: any) => {
    try {
      const qcPayload = {
        fertilizationDate: new Date(date as string).toISOString(),
        fertilizationSiteId: siteId as string,
        shipGroupId: shipGroup.shipGroupId,
        qcEHMDate10ml,
        qcEHMDate20ml,
        qcEHMDate40ml,
        qcEHMDate125ml,
        qcEHMDate250ml,
        qcShippingTubeExpirationDate,
      };

      const response = await saveQcFreshShipment(qcPayload);

      if (!response?.success) {
        throw new Error(response?.message);
      }
    } catch (e) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: `Error occurred while updating QC Data. Please include Shipping Expiration Date. ${e}`,
          sticky: false,
        }),
      );
      throw e;
    }
  };

  const handleSaveQCData = async () => {
    if (shipmentGroupsData) {
      try {
        const allShipGroups = [
          ...shipmentGroupsData.activeShipGroups,
          ...shipmentGroupsData.signedOffShipGroups,
        ];

        await Promise.all(allShipGroups.map(saveQcData));

        if (isQcSaveFixEnabled) {
          invalidateCache(['QcFreshShipment']);
        }

        dispatch({
          type: FreshShipmentActions.UPDATE_QC_LOADED,
          qcLoaded: true,
        });

        onHide();
      } catch (e) {
        console.error('Failed to save QC data:', e);
      }
    }
  };

  const footer = (
    <div>
      <Button
        severity="secondary"
        label="Cancel"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
      />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={handleSaveQCData}
        loading={isQcLoading}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      ref={dialogRef}
      header="QC Information"
      visible={visible}
      footer={footer}
      className="fresh-shipment-qc-information-dialog"
      onHide={onHide}
      onShow={() =>
        setDialogContainer(dialogRef.current?.getMask() ?? undefined)
      }
    >
      <div className="fresh-shipment-qc-information-dialog__fields">
        <div className="fresh-shipment-qc-information-dialog__field">
          <label htmlFor="qcEhmDate">QC EHM Exp Date (10ml)</label>
          <Calendar
            container={dialogContainer}
            id="qcEhmDate10"
            value={qcEHMDate10ml}
            onChange={(e) => setQcEHMDate10ml(e.value as Date)}
            showIcon
          />
        </div>
        <div className="fresh-shipment-qc-information-dialog__field">
          <label htmlFor="qcEhmDate">QC EHM Exp Date (20ml)</label>
          <Calendar
            container={dialogContainer}
            id="qcEhmDate20ml"
            value={qcEHMDate20ml}
            onChange={(e) => setQcEHMDate20ml(e.value as Date)}
            showIcon
          />
        </div>
        <div className="fresh-shipment-qc-information-dialog__field">
          <label htmlFor="qcEhmDate">QC EHM Exp Date (40ml)</label>
          <Calendar
            container={dialogContainer}
            id="qcEhmDate40ml"
            value={qcEHMDate40ml}
            onChange={(e) => setQcEHMDate40ml(e.value as Date)}
            showIcon
          />
        </div>{' '}
        <div className="fresh-shipment-qc-information-dialog__field">
          <label htmlFor="qcEhmDate">QC EHM Exp Date (125 mL)</label>
          <Calendar
            container={dialogContainer}
            id="qcEHMDate125ml"
            value={qcEHMDate125ml}
            onChange={(e) => setqcEHMDate125ml(e.value as Date)}
            showIcon
          />
        </div>
        <div className="fresh-shipment-qc-information-dialog__field">
          <label htmlFor="qcEhmDate">QC EHM Exp Date (250 mL)</label>
          <Calendar
            container={dialogContainer}
            id="qcEhmDate250ml"
            value={qcEHMDate250ml}
            onChange={(e) => setQcEHMDate250ml(e.value as Date)}
            showIcon
          />
        </div>
        <div className="fresh-shipment-qc-information-dialog__field">
          <label htmlFor="qcEhmDate">Shipping Tube Expiration Date</label>
          <Calendar
            container={dialogContainer}
            id="qcShippingTubeExpirationDate"
            value={qcShippingTubeExpirationDate}
            onChange={(e) => setQcShippingTubeExpirationDate(e.value as Date)}
            showIcon
          />
        </div>
      </div>
    </Dialog>
  );
};
